import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Moon, Sun, X } from "lucide-react";
import { useTheme } from "@/lib/theme";
import { cn } from "@/lib/utils";

export const NAV = [
  { to: "/work", label: "Work" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const ThemeToggle = () => {
  const { theme, toggle } = useTheme();
  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="theme-toggle-button"
      aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
      className="grid h-10 w-10 place-items-center rounded-full border border-line/20 text-fg transition-[border-color,color,transform] duration-300 hover:rotate-12 hover:border-gold hover:text-gold"
    >
      {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
};

export const Logo = () => (
  <Link to="/" data-testid="header-logo-link" className="group flex items-baseline font-display text-2xl tracking-[0.08em]" aria-label="Lutazzi — home">
    LUTAZZI<span className="text-gold transition-colors duration-300 group-hover:text-turq">.</span>
  </Link>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 24);
    f();
    window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, []);
  useEffect(() => {
    if (!open) return;
    const k = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500",
        scrolled || open ? "border-b border-line/10 bg-canvas/75 backdrop-blur-xl" : "border-b border-transparent"
      )}
    >
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:rounded focus:bg-turq focus:px-4 focus:py-2 focus:text-onturq">Skip to content</a>
      <div className="container-x flex h-[72px] items-center justify-between gap-6">
        <Logo />
        <nav aria-label="Primary" className="hidden items-center gap-9 md:flex">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) => cn("link-underline pb-1 text-sm tracking-wide transition-colors", isActive ? "text-fg [background-size:100%_1px]" : "text-silver hover:text-fg")}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <Link to="/contact?type=freelance" data-testid="header-start-project-button" className="hidden rounded-full bg-turq px-5 py-2.5 text-sm font-medium text-onturq transition-colors duration-300 hover:bg-fg hover:text-canvas lg:inline-flex">
            Start a Project
          </Link>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-button"
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? "Close menu" : "Open menu"}
            className="grid h-10 w-10 place-items-center rounded-full border border-line/20 md:hidden"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            id="mobile-menu"
            aria-label="Mobile"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-t border-line/10 md:hidden"
            data-testid="mobile-menu"
          >
            <ul className="container-x space-y-1 py-8">
              {[{ to: "/", label: "Home" }, ...NAV].map((n, i) => (
                <li key={n.to}>
                  <NavLink to={n.to} end data-testid={`mobile-nav-link-${n.label.toLowerCase()}`} className={({ isActive }) => cn("flex items-baseline gap-4 py-2 font-display text-4xl", isActive ? "text-turq" : "text-fg")}>
                    <span className="font-mono text-xs text-gold">{String(i + 1).padStart(2, "0")}</span>
                    {n.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
